import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { formatAge } from '../../utils/formatAge';
import { raceChip, personalityChip } from '../../utils/dossierFlavor';
import { DossierFlavorChip } from '../dossier/DossierFlavorChip';
import { deriveHeadlineStats } from '../../utils/groupIntelByTarget';
import type { TargetGroup } from '../../utils/groupIntelByTarget';
import { INTEL_TYPE_META } from './intelTypeMeta';
import type { IntelType } from '../../types/intel';

const MONO = "'JetBrains Mono', monospace";

interface IntelTargetCardProps {
  group: TargetGroup;
  onSelect: (targetProvinceId: string) => void;
}

/**
 * SF-157 Component 4: stateless presentational list-item for one scouted `TargetGroup` — name/
 * location, freshest-report age, a Race + Personality connected chip pair, one colored type chip
 * per report type present, and the group's headline stats across four fixed, non-wrapping rows
 * (v6/v7). Each row renders only when it has data, but which row a stat belongs to is fixed.
 */
export function IntelTargetCard({ group, onSelect }: IntelTargetCardProps) {
  const stats = deriveHeadlineStats(group);
  const presentTypes = Array.from(new Set(group.reports.map((r) => r.type))) as IntelType[];
  const accessibleName = `${group.targetProvinceName} (${group.targetKingdomLocation}) — scouted target`;
  const hasCb = group.reports.some((r) => r.type === 'CB');

  const hasNetworthOrLand = stats.networth != null || stats.land != null;
  const hasOffenseOrDefense = stats.offensePoints != null || stats.defensePoints != null;
  const hasResources = hasCb;
  const hasMisc = stats.rawTPA != null || stats.researchRate != null || stats.homeDefense != null;

  return (
    <button
      type="button"
      onClick={() => onSelect(group.targetProvinceId)}
      aria-label={accessibleName}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 12px',
        minHeight: 44,
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        cursor: 'pointer',
        marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: COLORS.text.primary }}>
          {group.targetProvinceName}{' '}
          <span style={{ color: COLORS.link, fontSize: 11, fontFamily: MONO, fontWeight: 400 }}>
            ({group.targetKingdomLocation})
          </span>
        </span>
        <span style={{ color: COLORS.text.muted, fontSize: 10, whiteSpace: 'nowrap' }}>
          {formatAge(new Date(group.freshestCapturedAt).toISOString())}
        </span>
      </div>

      {group.race != null && (
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <DossierFlavorChip meta={raceChip(group.race)} />
          {hasCb && group.personality != null && <DossierFlavorChip meta={personalityChip(group.personality)} />}
        </div>
      )}

      {presentTypes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {presentTypes.map((type) => {
            const meta = INTEL_TYPE_META[type];
            return (
              <span
                key={type}
                style={{
                  display: 'inline-block',
                  padding: '1px 6px',
                  borderRadius: 3,
                  fontSize: 10,
                  fontWeight: 700,
                  color: meta.fg,
                  background: meta.bg,
                  border: `1px solid ${meta.bd}`,
                }}
              >
                {meta.label}
              </span>
            );
          })}
        </div>
      )}

      {hasNetworthOrLand && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {stats.networth != null && <StatTile label="Networth" value={formatNumber(stats.networth)} color={COLORS.gold} />}
          {stats.land != null && <StatTile label="Land (acres)" value={formatNumber(stats.land)} />}
        </div>
      )}

      {hasOffenseOrDefense && (
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          {stats.offensePoints != null && <StatTile label="Offense" value={formatNumber(stats.offensePoints)} color="#e8a04f" />}
          {stats.defensePoints != null && <StatTile label="Defense" value={formatNumber(stats.defensePoints)} color={COLORS.link} />}
        </div>
      )}

      {hasResources && (
        <div style={{ marginTop: 6, borderTop: `1px solid ${COLORS.border}`, paddingTop: 6 }}>
          {stats.gold != null && <LV label="Gold (gc)" value={formatNumber(stats.gold)} color={COLORS.gold} />}
          {stats.runes != null && <LV label="Runes" value={formatNumber(stats.runes)} />}
          {stats.food != null && <LV label="Food (bushels)" value={formatNumber(stats.food)} />}
          {stats.tradeBalance != null && <LV label="Trade Balance (gc)" value={formatNumber(stats.tradeBalance)} muted />}
        </div>
      )}

      {hasMisc && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6, borderTop: `1px solid ${COLORS.border}`, paddingTop: 6 }}>
          {stats.rawTPA != null && <LV label="Raw TPA" value={stats.rawTPA.toFixed(2)} />}
          {stats.researchRate != null && <LV label="Research Rate" value={formatEnumLabel(stats.researchRate)} />}
          {stats.homeDefense != null && <LV label="Home Defense" value={formatNumber(Math.round(stats.homeDefense))} />}
        </div>
      )}
    </button>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ flex: 1, background: COLORS.cardAlt, borderRadius: 6, padding: '6px 8px' }}>
      <div style={{ color: COLORS.text.tertiary, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color: color ?? COLORS.text.primary, fontSize: 14, fontWeight: 700, fontFamily: MONO }}>{value}</div>
    </div>
  );
}

function LV({ label, value, color, muted }: { label: string; value: string; color?: string; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '1px 0' }}>
      <span style={{ color: COLORS.text.tertiary, fontSize: 11 }}>{label}</span>
      <span style={{ color: muted ? COLORS.text.muted : (color ?? COLORS.text.primary), fontSize: 11, fontFamily: MONO }}>
        {value}
      </span>
    </div>
  );
}
